import { readCookie } from "@lib/analytics/fbCapiContext";
import {
  type PurchaseOption,
  VALID_PURCHASE_OPTIONS,
} from "@lib/api/checkout.types";
import { getDiscountQueryOptions } from "@lib/helpers/getDiscountQueryOptions";
import { getProductPricingQueryOptions } from "@lib/helpers/getProductPricingQueryOptions";
import { startBraintreeChunkPreload } from "@pages/checkout/braintreeChunk";
import { queryClient } from "@utils/QueryClientProvider";

// Pre-mount checkout data prefetch (R2/R18, plan 2026-07-07-004).
//
// The pricing GET hits a Render host with a multi-second cold start in prod,
// and checkout.tsx's useQuery only fires after JS parse + i18n + React mount.
// Dispatching the same query from main.tsx overlaps the round-trip with app
// boot; the mount-time useQuery then hits the warm react-query cache
// (module-level queryClient, staleTime 5min) instead of refetching. The
// mount-time query stays fully self-sufficient — SPA-internal navigation and
// prefetch failure both fall back to today's serial behavior.

// The app is reachable at both URL forms: "/checkout/pf" (prod entry;
// CloudFront rewrites to /members/ origin-side, browser pathname unchanged)
// and "/members/checkout/pf" (direct UAT/foxhole URLs). Note this is wider
// than fireEarlyCheckoutTracking's "/checkout*"-only predicate — begin_checkout
// parity across arms is measured on the prod form, but the prefetch should
// engage wherever the checkout actually loads.
export const isCheckoutPathname = (pathname: string): boolean => {
  const path = pathname.startsWith("/members/")
    ? pathname.slice("/members".length)
    : pathname;
  return path === "/checkout" || path.startsWith("/checkout/");
};

// Single source of truth for the invalid-option default. checkout.tsx
// redirects invalid/missing ?option= to "series" after mount; the prefetch
// must warm the post-redirect key, not skip (the deliberate divergence from
// earlyCheckoutTracking, which skips because it can't replicate the redirect).
// checkout.tsx consumes this same function for its validity check so the two
// can't drift — a drifted default would double-fetch the cold host serially.
export const resolveCheckoutOption = (
  rawOption: string,
): { option: PurchaseOption; isValid: boolean } => {
  const isValid = VALID_PURCHASE_OPTIONS.includes(rawOption as PurchaseOption);
  return {
    option: isValid ? (rawOption as PurchaseOption) : "series",
    isValid,
  };
};

const mark = (name: string): void => {
  if (typeof performance !== "undefined" && performance.mark) {
    performance.mark(name);
  }
};

export const prefetchCheckoutData = (): void => {
  // Belt for the module-scope call site in main.tsx: this runs BEFORE React
  // mounts, and the documented contract is that no failure here may ever
  // block the mount — the mount-time useQuery is always the fallback.
  try {
    prefetchCheckoutDataUnsafe();
  } catch {
    // Swallowed by contract; the mount-time queries refetch from scratch.
  }
};

const prefetchCheckoutDataUnsafe = (): void => {
  if (typeof window === "undefined") return;
  if (!isCheckoutPathname(window.location.pathname)) return;

  const params = new URLSearchParams(window.location.search);
  const { option } = resolveCheckoutOption(params.get("option") ?? "");

  mark("omg:pricing-prefetch-dispatched");
  // prefetchQuery never rejects (react-query swallows queryFn errors for
  // prefetches); the catch is a belt against a throwing mock/env. A failed
  // prefetch simply leaves the cache cold and the mount-time useQuery
  // refetches — today's serial behavior.
  void queryClient
    .prefetchQuery(getProductPricingQueryOptions({ enabled: true, option }))
    .catch(() => undefined);

  const discountCode =
    params.get("discount_source") || readCookie("discount_source") || null;
  if (discountCode) {
    void queryClient
      .prefetchQuery(getDiscountQueryOptions(discountCode))
      .catch(() => undefined);
  }

  // Start the Braintree Drop-in chunk download now too (U2/R1) — same
  // memoized promise the lazy() render path consumes, so this is a head
  // start, never a duplicate fetch.
  startBraintreeChunkPreload();
};
